import {
  type Classroom,
  type ClassroomActor,
  findMember,
  hasRequested,
  type JoinableRole,
  type JoinRequest,
} from '#/state/classrooms'
import {type ClassroomViewer} from '#/state/queries/classrooms'

function askedRole(actor: ClassroomActor): JoinableRole {
  if (actor.accountType === 'learner') return 'student'
  if (actor.accountType === 'parent') return 'parent'
  return 'teacher'
}

/**
 * The room as the shared rule helpers need to see it for this reader.
 *
 * The server sends a room without its roster, while the helpers the room's tabs,
 * menu and dialogs still use (`findMember`, `hasRequested`,
 * `isClassroomModerator`) read the reader's own seat off `members`, `requests`
 * and `moderators`. Folding the server's answer about the reader into those
 * lists keeps every one of those rules right for the person looking, without
 * pretending to know anybody else's seat.
 */
export function roomForViewer(
  classroom: Classroom,
  viewer: ClassroomViewer,
  actor: ClassroomActor | undefined,
  requests?: JoinRequest[],
): Classroom {
  if (!actor) return {...classroom, requests: requests ?? classroom.requests}
  const seated =
    viewer.role && viewer.role !== 'admin' && !findMember(classroom, actor.did)
  const members = seated
    ? [
        ...classroom.members,
        {
          did: actor.did,
          handle: actor.handle,
          displayName: actor.displayName,
          avatar: actor.avatar,
          role: viewer.role!,
          joinedAt: classroom.createdAt,
          isModerator: viewer.isModerator,
        },
      ]
    : classroom.members
  const moderators =
    viewer.isModerator && !(classroom.moderators ?? []).includes(actor.did)
      ? [...(classroom.moderators ?? []), actor.did]
      : classroom.moderators
  const pending = viewer.request && !viewer.request.declinedAt
  const ownRequest: JoinRequest | null =
    pending && !hasRequested(classroom, actor.did)
      ? {
          did: actor.did,
          handle: actor.handle,
          displayName: actor.displayName,
          avatar: actor.avatar,
          role: askedRole(actor),
          requestedAt: viewer.request!.createdAt,
        }
      : null
  const baseRequests = requests ?? classroom.requests
  const declinedAt = viewer.request?.declinedAt
  return {
    ...classroom,
    members,
    moderators,
    requests: ownRequest ? [...baseRequests, ownRequest] : baseRequests,
    declined:
      declinedAt && !classroom.declined?.some(d => d.did === actor.did)
        ? [
            ...(classroom.declined ?? []),
            {did: actor.did, handle: actor.handle, at: declinedAt},
          ]
        : classroom.declined,
  }
}
